(() => {
  const SELECTORS = {
    carousel: '[data-product-carousel]',
    track: '[data-carousel-track]',
    prev: '[data-carousel-prev]',
    next: '[data-carousel-next]',
    progress: '[data-carousel-progress]',
    autoplayToggle: '[data-carousel-autoplay-toggle]',
  };

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const instances = new WeakMap();
  const dragThreshold = 8;
  const interactionResumeDelay = 300;
  const scrollIdleResumeDelay = 500;
  const maxAutoplayFrameDelta = 32;
  const verticalWheelResetDelay = 160;

  class ProductCarousel {
    constructor(element) {
      this.element = element;
      this.track = element.querySelector(SELECTORS.track);
      this.prevButton = element.querySelector(SELECTORS.prev);
      this.nextButton = element.querySelector(SELECTORS.next);
      this.progress = element.querySelector(SELECTORS.progress);
      this.autoplayToggle = element.querySelector(SELECTORS.autoplayToggle);
      this.autoplayRequested = element.dataset.autoplay === 'true';
      this.autoplaySpeed = Math.max(parseInt(element.dataset.autoplaySpeed, 10) || 12000, 3000);
      this.continuous = element.dataset.continuousLoop === 'true';
      this.timer = null;
      this.animationFrame = null;
      this.resizeFrame = null;
      this.lastFrameTime = null;
      this.paused = this.autoplayRequested && reduceMotion.matches;
      this.userPaused = this.paused;
      this.reducedMotionPaused = this.paused;
      this.reducedMotionOverride = false;
      this.pointerDown = false;
      this.nativeScrollInteracting = false;
      this.dragging = false;
      this.suppressNextClick = false;
      this.startX = 0;
      this.startY = 0;
      this.lastX = 0;
      this.cycleWidth = 0;
      this.centralStart = 0;
      this.stepWidth = 0;
      this.position = 0;
      this.scrollEndTimer = null;
      this.resumeTimer = null;
      this.clickResetTimer = null;
      this.verticalWheelTimer = null;
      this.clones = [];

      if (!this.track) return;

      if (this.continuous) this.buildLoop();
      this.bindEvents();
      this.update();
      this.updateAutoplayToggle();
      this.startAutoplay();
    }

    get autoplay() {
      return this.autoplayRequested && (!reduceMotion.matches || this.reducedMotionOverride);
    }

    bindEvents() {
      this.onPrevClick = () => {
        this.pauseForInteraction();
        this.scrollByStep(-1);
      };
      this.onNextClick = () => {
        this.pauseForInteraction();
        this.scrollByStep(1);
      };
      this.onAutoplayToggleClick = () => {
        if (this.userPaused) this.playAutoplay();
        else this.pauseAutoplay();
      };
      this.onScroll = () => {
        if (!this.continuous) {
          this.update();
        } else if (!this.animationFrame && !this.pointerDown) {
          this.position = this.normalizeLoopPosition(this.track.scrollLeft);
          if (this.nativeScrollInteracting) this.pauseForInteraction(scrollIdleResumeDelay);
        }
      };
      this.onWheel = (event) => this.handleWheel(event);
      this.onKeyDown = (event) => {
        if (['ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown'].includes(event.key)) {
          this.pauseForInteraction();
        }
      };
      this.onPointerDown = (event) => this.handlePointerDown(event);
      this.onPointerMove = (event) => this.handlePointerMove(event);
      this.onPointerUp = (event) => this.handlePointerUp(event);
      this.onClickCapture = (event) => this.handleClickCapture(event);
      this.onDragStart = (event) => {
        if (this.continuous) event.preventDefault();
      };
      this.onMouseEnter = () => {
        if (!this.continuous) this.pause();
      };
      this.onMouseLeave = () => {
        if (!this.continuous) this.resume();
      };
      this.onFocusIn = () => {
        if (!this.continuous) this.pause();
      };
      this.onFocusOut = () => {
        if (!this.continuous) this.resume();
      };
      this.onResize = () => {
        if (!this.continuous || this.resizeFrame) return;
        this.resizeFrame = window.requestAnimationFrame(() => {
          this.resizeFrame = null;
          this.buildLoop(true);
        });
      };
      this.onReduceMotionChange = () => {
        this.stopAutoplay();
        window.clearTimeout(this.resumeTimer);
        if (reduceMotion.matches && !this.reducedMotionOverride) {
          this.userPaused = true;
          this.paused = true;
          this.reducedMotionPaused = true;
        } else if (!reduceMotion.matches && this.reducedMotionPaused) {
          this.userPaused = false;
          this.paused = false;
          this.reducedMotionPaused = false;
        }
        this.updateAutoplayToggle();
        this.startAutoplay();
      };
      this.onVisibilityChange = () => {
        if (document.visibilityState === 'hidden') {
          this.stopAutoplay();
        } else {
          this.startAutoplay();
        }
      };

      this.prevButton?.addEventListener('click', this.onPrevClick);
      this.nextButton?.addEventListener('click', this.onNextClick);
      this.autoplayToggle?.addEventListener('click', this.onAutoplayToggleClick);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.track.addEventListener('wheel', this.onWheel, { passive: false });
      this.track.addEventListener('keydown', this.onKeyDown);
      this.track.addEventListener('pointerdown', this.onPointerDown);
      this.track.addEventListener('pointermove', this.onPointerMove);
      this.track.addEventListener('pointerup', this.onPointerUp);
      this.track.addEventListener('pointercancel', this.onPointerUp);
      this.track.addEventListener('click', this.onClickCapture, true);
      this.track.addEventListener('dragstart', this.onDragStart);
      this.element.addEventListener('mouseenter', this.onMouseEnter);
      this.element.addEventListener('mouseleave', this.onMouseLeave);
      this.element.addEventListener('focusin', this.onFocusIn);
      this.element.addEventListener('focusout', this.onFocusOut);
      window.addEventListener('resize', this.onResize, { passive: true });
      if (this.continuous) {
        window.addEventListener('pointerup', this.onPointerUp);
        window.addEventListener('pointercancel', this.onPointerUp);
      }
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      if (reduceMotion.addEventListener) reduceMotion.addEventListener('change', this.onReduceMotionChange);
      else reduceMotion.addListener(this.onReduceMotionChange);
    }

    destroy() {
      if (!this.track) return;

      this.stopAutoplay();
      window.cancelAnimationFrame(this.resizeFrame);
      window.clearTimeout(this.scrollEndTimer);
      window.clearTimeout(this.resumeTimer);
      window.clearTimeout(this.clickResetTimer);
      window.clearTimeout(this.verticalWheelTimer);
      this.prevButton?.removeEventListener('click', this.onPrevClick);
      this.nextButton?.removeEventListener('click', this.onNextClick);
      this.autoplayToggle?.removeEventListener('click', this.onAutoplayToggleClick);
      this.track.removeEventListener('scroll', this.onScroll);
      this.track.removeEventListener('wheel', this.onWheel);
      this.track.removeEventListener('keydown', this.onKeyDown);
      this.track.removeEventListener('pointerdown', this.onPointerDown);
      this.track.removeEventListener('pointermove', this.onPointerMove);
      this.track.removeEventListener('pointerup', this.onPointerUp);
      this.track.removeEventListener('pointercancel', this.onPointerUp);
      this.track.removeEventListener('click', this.onClickCapture, true);
      this.track.removeEventListener('dragstart', this.onDragStart);
      this.element.removeEventListener('mouseenter', this.onMouseEnter);
      this.element.removeEventListener('mouseleave', this.onMouseLeave);
      this.element.removeEventListener('focusin', this.onFocusIn);
      this.element.removeEventListener('focusout', this.onFocusOut);
      window.removeEventListener('resize', this.onResize);
      if (this.continuous) {
        window.removeEventListener('pointerup', this.onPointerUp);
        window.removeEventListener('pointercancel', this.onPointerUp);
      }
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      if (reduceMotion.removeEventListener) reduceMotion.removeEventListener('change', this.onReduceMotionChange);
      else reduceMotion.removeListener(this.onReduceMotionChange);
      this.removeClones();
    }

    getOriginalItems() {
      return Array.from(this.track.children).filter((item) => !item.hasAttribute('data-carousel-clone'));
    }

    makeClone(item) {
      const clone = item.cloneNode(true);
      clone.setAttribute('data-carousel-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('role', 'presentation');
      clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
      clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((node) => {
        node.setAttribute('tabindex', '-1');
      });
      clone.querySelectorAll('img').forEach((image) => {
        image.draggable = false;
        image.removeAttribute('loading');
      });
      return clone;
    }

    removeClones() {
      this.track.querySelectorAll('[data-carousel-clone]').forEach((clone) => clone.remove());
      this.clones = [];
    }

    measureCycle(items) {
      if (!items.length) return 0;
      const styles = window.getComputedStyle(this.track);
      const gap = parseFloat(styles.columnGap || styles.gap) || 0;
      const first = items[0];
      const last = items[items.length - 1];
      return last.offsetLeft + last.getBoundingClientRect().width - first.offsetLeft + gap;
    }

    buildLoop(preservePosition = false) {
      const oldCycleWidth = this.cycleWidth;
      const oldCentralStart = this.centralStart;
      const oldPhase = oldCycleWidth
        ? this.modulo(this.position - oldCentralStart, oldCycleWidth) / oldCycleWidth
        : 0;

      this.removeClones();
      const originals = this.getOriginalItems();
      this.cycleWidth = this.measureCycle(originals);
      if (!this.cycleWidth) return;

      const styles = window.getComputedStyle(this.track);
      const gap = parseFloat(styles.columnGap || styles.gap) || 0;
      this.stepWidth = originals[0].getBoundingClientRect().width + gap;

      const leadingCopyCount = Math.max(1, Math.ceil(this.track.clientWidth / this.cycleWidth));
      const trailingCopyCount = leadingCopyCount + 1;
      const leading = document.createDocumentFragment();
      const trailing = document.createDocumentFragment();
      let firstTrailingClone = null;

      for (let copy = 0; copy < leadingCopyCount; copy += 1) {
        originals.forEach((item) => {
          const before = this.makeClone(item);
          this.clones.push(before);
          leading.appendChild(before);
        });
      }

      for (let copy = 0; copy < trailingCopyCount; copy += 1) {
        originals.forEach((item) => {
          const after = this.makeClone(item);
          if (!firstTrailingClone) firstTrailingClone = after;
          this.clones.push(after);
          trailing.appendChild(after);
        });
      }

      this.track.insertBefore(leading, originals[0]);
      this.track.appendChild(trailing);

      const paddingLeft = parseFloat(styles.paddingLeft) || 0;
      this.centralStart = originals[0].offsetLeft - paddingLeft;
      if (firstTrailingClone) {
        this.cycleWidth = firstTrailingClone.offsetLeft - originals[0].offsetLeft;
      }
      this.setPosition(this.centralStart + (preservePosition ? oldPhase * this.cycleWidth : 0));
    }

    modulo(value, divisor) {
      return ((value % divisor) + divisor) % divisor;
    }

    normalizeLoopPosition(position) {
      if (!this.continuous || !this.cycleWidth) return position;
      const offset = position - this.centralStart;
      if (offset >= this.cycleWidth || offset < 0) {
        return this.centralStart + this.modulo(offset, this.cycleWidth);
      }
      return position;
    }

    setPosition(position) {
      this.position = this.normalizeLoopPosition(position);
      this.track.scrollLeft = this.position;
    }

    getMaxScroll() {
      return Math.max(this.track.scrollWidth - this.track.clientWidth, 0);
    }

    getStep() {
      const firstItem = this.getOriginalItems()[0];
      if (!firstItem) return this.track.clientWidth * 0.85;

      const styles = window.getComputedStyle(this.track);
      const gap = parseFloat(styles.columnGap || styles.gap) || 0;
      return firstItem.getBoundingClientRect().width + gap;
    }

    getScrollOptions(left) {
      return {
        left,
        behavior: reduceMotion.matches ? 'auto' : 'smooth',
      };
    }

    scrollByStep(direction) {
      const maxScroll = this.getMaxScroll();
      if (maxScroll <= 0) return;

      let nextPosition = this.track.scrollLeft + this.getStep() * direction;
      if (direction > 0 && nextPosition >= maxScroll - 4) nextPosition = maxScroll;
      if (direction < 0 && nextPosition <= 4) nextPosition = 0;

      this.track.scrollTo(this.getScrollOptions(Math.max(0, Math.min(nextPosition, maxScroll))));
    }

    advance() {
      const maxScroll = this.getMaxScroll();
      if (maxScroll <= 0) return;

      if (this.track.scrollLeft >= maxScroll - 4) {
        this.track.scrollTo(this.getScrollOptions(0));
      } else {
        this.scrollByStep(1);
      }
    }

    update() {
      if (this.continuous) {
        this.setPosition(this.position);
        return;
      }

      const maxScroll = this.getMaxScroll();
      const progress = maxScroll > 0 ? this.track.scrollLeft / maxScroll : 1;

      this.element.style.setProperty('--carousel-progress', progress.toFixed(4));
      if (this.progress) this.progress.setAttribute('aria-valuenow', Math.round(progress * 100));
      if (this.prevButton) this.prevButton.disabled = this.track.scrollLeft <= 2;
      if (this.nextButton) this.nextButton.disabled = this.track.scrollLeft >= maxScroll - 2;
    }

    pause() {
      this.paused = true;
      this.stopAutoplay();
    }

    resume() {
      if (this.userPaused) return;
      this.paused = false;
      this.startAutoplay();
    }

    pauseAutoplay() {
      this.userPaused = true;
      this.paused = true;
      this.reducedMotionPaused = false;
      window.clearTimeout(this.resumeTimer);
      this.nativeScrollInteracting = false;
      this.stopAutoplay();
      this.updateAutoplayToggle();
    }

    playAutoplay() {
      this.userPaused = false;
      this.paused = false;
      this.reducedMotionPaused = false;
      this.reducedMotionOverride = true;
      window.clearTimeout(this.resumeTimer);
      this.startAutoplay();
      this.updateAutoplayToggle();
    }

    updateAutoplayToggle() {
      if (!this.autoplayToggle) return;

      const isPaused = this.userPaused;
      const pauseLabel = this.autoplayToggle.dataset.pauseLabel || 'Pause carousel';
      const playLabel = this.autoplayToggle.dataset.playLabel || 'Play carousel';
      this.autoplayToggle.setAttribute('aria-label', isPaused ? playLabel : pauseLabel);
      this.autoplayToggle.classList.toggle('is-paused', isPaused);
    }

    stopAutoplay() {
      window.clearInterval(this.timer);
      this.timer = null;
      window.cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
      this.lastFrameTime = null;
    }

    pauseForInteraction(resumeDelay) {
      if (!this.autoplayRequested) return;

      this.stopAutoplay();
      window.clearTimeout(this.resumeTimer);
      if (!this.paused) {
        const delay = this.continuous ? interactionResumeDelay : this.autoplaySpeed * 1.5;
        const resolvedDelay = resumeDelay ?? delay;
        this.resumeTimer = window.setTimeout(() => {
          this.nativeScrollInteracting = false;
          this.startAutoplay();
        }, resolvedDelay);
      }
    }

    startAutoplay() {
      if (
        !this.autoplay ||
        this.paused ||
        this.pointerDown ||
        document.visibilityState === 'hidden'
      )
        return;

      if (this.continuous) {
        if (this.animationFrame || !this.cycleWidth) return;
        this.animationFrame = window.requestAnimationFrame((time) => this.animate(time));
        return;
      }

      if (!this.timer) this.timer = window.setInterval(() => this.advance(), this.autoplaySpeed);
    }

    animate(time) {
      if (!this.autoplay || this.paused || this.pointerDown || document.visibilityState === 'hidden') {
        this.animationFrame = null;
        this.lastFrameTime = null;
        return;
      }

      if (this.lastFrameTime !== null) {
        const elapsed = Math.min(time - this.lastFrameTime, maxAutoplayFrameDelta);
        this.setPosition(this.position + (this.stepWidth / this.autoplaySpeed) * elapsed);
      }
      this.lastFrameTime = time;
      this.animationFrame = window.requestAnimationFrame((nextTime) => this.animate(nextTime));
    }

    handleWheel(event) {
      const absDeltaX = Math.abs(event.deltaX);
      const absDeltaY = Math.abs(event.deltaY);
      const isShiftHorizontal = event.shiftKey && absDeltaY > 0;
      const isHorizontalWheel = absDeltaX > absDeltaY || isShiftHorizontal;

      if (!isHorizontalWheel) {
        this.track.classList.add('is-vertical-wheeling');
        window.clearTimeout(this.verticalWheelTimer);
        this.verticalWheelTimer = window.setTimeout(() => {
          this.track.classList.remove('is-vertical-wheeling');
        }, verticalWheelResetDelay);
        return;
      }

      this.track.classList.remove('is-vertical-wheeling');
      this.pauseForInteraction();

      const wheelDelta = absDeltaX > absDeltaY ? event.deltaX : event.deltaY;
      if (wheelDelta === 0) return;

      event.preventDefault();
      this.setPosition(this.track.scrollLeft + wheelDelta);
    }

    handlePointerDown(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (event.pointerType !== 'mouse') {
        this.nativeScrollInteracting = true;
        this.pauseForInteraction(scrollIdleResumeDelay);
        return;
      }

      this.pointerDown = true;
      this.dragging = false;
      this.suppressNextClick = false;
      this.startX = event.clientX;
      this.startY = event.clientY;
      this.lastX = event.clientX;
      if (this.continuous) this.position = this.track.scrollLeft;
      this.pauseForInteraction();
    }

    handlePointerMove(event) {
      if (!this.pointerDown) return;

      const deltaX = event.clientX - this.startX;
      const deltaY = event.clientY - this.startY;
      const absDeltaX = Math.abs(deltaX);
      const absDeltaY = Math.abs(deltaY);

      if (!this.dragging) {
        if (absDeltaX < dragThreshold && absDeltaY < dragThreshold) return;
        if (absDeltaY >= absDeltaX) return;

        this.dragging = true;
        this.suppressNextClick = true;
        this.track.classList.add('is-dragging');
        if (this.continuous) this.track.setPointerCapture?.(event.pointerId);
      }

      event.preventDefault();
      const movement = event.clientX - this.lastX;
      if (this.continuous) this.setPosition(this.position - movement);
      else this.track.scrollLeft -= movement;
      this.lastX = event.clientX;
    }

    handlePointerUp(event) {
      if (!this.pointerDown) return;

      this.pointerDown = false;
      if (this.track.hasPointerCapture?.(event.pointerId)) this.track.releasePointerCapture(event.pointerId);
      window.clearTimeout(this.scrollEndTimer);
      window.clearTimeout(this.clickResetTimer);
      this.scrollEndTimer = window.setTimeout(() => {
        this.dragging = false;
        this.track.classList.remove('is-dragging');
      }, 80);
      this.clickResetTimer = window.setTimeout(() => {
        this.suppressNextClick = false;
      }, 400);
      this.pauseForInteraction();
    }

    handleClickCapture(event) {
      if (!this.suppressNextClick) return;

      event.preventDefault();
      event.stopPropagation();
      window.clearTimeout(this.clickResetTimer);
      this.suppressNextClick = false;
    }
  }

  function initProductCarousels(root = document) {
    root.querySelectorAll(SELECTORS.carousel).forEach((carousel) => {
      instances.get(carousel)?.destroy();
      instances.set(carousel, new ProductCarousel(carousel));
    });
  }

  window.initProductCarousels = initProductCarousels;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initProductCarousels());
  } else {
    initProductCarousels();
  }

  document.addEventListener('shopify:section:load', (event) => initProductCarousels(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll(SELECTORS.carousel).forEach((carousel) => {
      instances.get(carousel)?.destroy();
      instances.delete(carousel);
    });
  });
})();
